import React from "react";
import styled, { css, keyframes } from "styled-components";

type HistoryEntry = {
  id: number;
  prompt: string;
  response: string;
};

type LyricsPromptModalProps = {
  open: boolean;
  title: string;
  mode: "edit" | "write";
  promptValue: string;
  onPromptChange: (value: string) => void;
  isLoading: boolean;
  error: string | null;
  response: string;
  history: HistoryEntry[];
  onSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
  onUse: (response: string) => void;
  onClose: () => void;
};

export const LyricsPromptModal: React.FC<LyricsPromptModalProps> = ({
  open,
  title,
  mode,
  promptValue,
  onPromptChange,
  isLoading,
  error,
  response,
  history,
  onSubmit,
  onUse,
  onClose,
}) => {
  if (!open) return null;

  const placeholder =
    mode === "edit"
      ? "Add guidance or paste lyrics you'd like to refine…"
      : "Describe the story, mood, or theme you'd like to compose…";

  return (
    <Overlay>
      <Container>
        <Header>
          <Title>{title}</Title>
          <CloseButton onClick={onClose}>×</CloseButton>
        </Header>

        <Content>
          <PromptForm onSubmit={onSubmit}>
            <PromptLabel>{mode === "edit" ? "Refinement prompt" : "Song concept prompt"}</PromptLabel>
            <PromptTextarea
              value={promptValue}
              onChange={(e) => onPromptChange(e.target.value)}
              placeholder={placeholder}
              disabled={isLoading}
            />
            <PromptActions>
              {error && <PromptError>{error}</PromptError>}
              <PrimaryButton type="submit" disabled={isLoading}>
                {isLoading ? "Generating…" : "Generate"}
              </PrimaryButton>
            </PromptActions>
          </PromptForm>

          <ResponseBlock>
            {isLoading && (
              <SpinnerOverlay>
                <Spinner />
              </SpinnerOverlay>
            )}
            {response ? (
              <ResponseText>{response}</ResponseText>
            ) : (
              <EmptyResponse>This is where response will appear.</EmptyResponse>
            )}
          </ResponseBlock>
        </Content>

        <Footer>
          <PrimaryButton onClick={() => onUse(response)} disabled={!response.trim()}>
            Use In Lyrics
          </PrimaryButton>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </Footer>
      </Container>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  padding: 16px;
`;

const Container = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: min(720px, 92vw);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #f1f5f9;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  font-size: 20px;
  cursor: pointer;
  color: #64748b;
  padding: 4px;

  &:hover {
    color: #0f172a;
  }
`;

const Content = styled.div`
  padding: 20px;
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Footer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 20px 20px;
  border-top: 1px solid #f1f5f9;
`;

const PromptForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PromptLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #475569;
`;

const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  font-size: 14px;
  color: #0f172a;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: #6777ff;
  }
`;

const PromptActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  min-height: 20px;
`;

const PromptError = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-right: auto;
`;

const ResponseBlock = styled.div`
  position: relative;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  padding: 20px;
  min-height: 220px;
  max-height: 320px;
  overflow: auto;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(248, 250, 252, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
`;

const ResponseText = styled.pre`
  margin: 0;
  font-size: 14px;
  color: #0f172a;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyResponse = styled.div`
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
`;

const HistorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HistoryEntryBlock = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
`;

const HistoryPrompt = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
`;

const HistoryResponse = styled.div`
  font-size: 13px;
  color: #0f172a;
  white-space: pre-wrap;
`;

const buttonStyles = css`
  border: none;
  border-radius: 12px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

const PrimaryButton = styled.button<{ disabled?: boolean }>`
  ${buttonStyles};
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  color: #ffffff;
  opacity: ${(p) => (p.disabled ? 0.6 : 1)};

  &:hover {
    opacity: ${(p) => (p.disabled ? 0.6 : 0.9)};
  }
`;

const SecondaryButton = styled.button`
  ${buttonStyles};
  background: #f1f5f9;
  color: #334155;

  &:hover {
    background: #e2e8f0;
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(103, 119, 255, 0.2);
  border-top-color: #6777FF;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 20px auto;
`;

